import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import SampleFirmwareInstallationStatus from '../../../types/sample-firmware-revision/SampleFirmwareInstallationStatus';
import SampleFirmwareRevision from '../../../types/sample-firmware-revision/SampleFirmwareRevision';
import SampleFirmwareRevisionFileDTO from '../../../types/sample-firmware-revision/SampleFirmwareRevisionFileDTO';
import SampleFirmwareRevisionWriteDTO from '../../../types/sample-firmware-revision/SampleFirmwareRevisionWriteDTO';
import SampleFirmwareUpgradeRequest from '../../../types/sample-firmware-upgrade/SampleFirmwareUpgradeRequest';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class SampleFirmwareRevisionService {

    private RESOURCE: string = 'firmware-revision';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches all sample firmware installation statuses from the backend.
     *
     * @return The sample firmware installation statuses.
     */
    public async getAllSampleFirmwareInstallationStatuses(): Promise<SampleFirmwareInstallationStatus[]> {
        return await this.baseService.get<SampleFirmwareInstallationStatus[]>(
            this.RESOURCE + '/all-installation-statuses');
    }

    /**
     * Fetches the sample firmware revisions from the backend.
     *
     * @param search The search query to filter the sample firmware revisions by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @return The sample firmware revisions.
     */
    public async getSampleFirmwareRevisions(search?: string, page: number = 0,
                                            size: number = 10): Promise<Page<SampleFirmwareRevision>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }

        return await this.baseService.get<Page<SampleFirmwareRevision>>(this.RESOURCE, params);
    }

    /**
     * Fetches multiple sample firmware revisions from the backend.
     *
     * @param ids The IDs of the sample firmware revisions to fetch.
     * @return The sample firmware revisions.
     */
    public async getMultipleSampleFirmwareRevisions(ids: number[]): Promise<SampleFirmwareRevision[]> {
        const params = new HttpParams().set('ids', ids.join(','));

        return await this.baseService.get<SampleFirmwareRevision[]>(this.RESOURCE + '/multiple', params);
    }

    /**
     * Fetches all sample firmware revisions from the backend.
     *
     * @return The sample firmware revisions.
     */
    public async getAllSampleFirmwareRevisions(): Promise<SampleFirmwareRevision[]> {
        return await this.baseService.get<SampleFirmwareRevision[]>(this.RESOURCE + '/all');
    }

    /**
     * Fetches a sample firmware revision from the backend.
     *
     * @param id The ID of the sample firmware revision to fetch.
     * @return The sample firmware revision.
     */
    public async getSampleFirmwareRevision(id: number): Promise<SampleFirmwareRevision> {
        return await this.baseService.get<SampleFirmwareRevision>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a sample firmware revision in the backend.
     *
     * @param sampleFirmwareRevision The sample firmware revision to create.
     * @param file The selected firmware revision file.
     * @return The response from the request.
     */
    public async createSampleFirmwareRevision(sampleFirmwareRevision: SampleFirmwareRevisionWriteDTO,
                                              file: File): Promise<void> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('data', new Blob([ JSON.stringify(sampleFirmwareRevision) ], { type: 'application/json' }));

        return await this.baseService.post<void>(this.RESOURCE, formData);
    }

    /**
     * Updates a sample firmware revision in the backend.
     *
     * @param sampleFirmwareRevision The sample firmware revision to update.
     * @param file The selected firmware revision file.
     * @return The response from the request.
     */
    public async updateSampleFirmwareRevision(sampleFirmwareRevision: SampleFirmwareRevisionWriteDTO,
                                              file: File | null): Promise<void> {
        const formData = new FormData();
        if (file != null) {
            formData.append('file', file, file.name);
        }
        formData.append('data', new Blob([ JSON.stringify(sampleFirmwareRevision) ], { type: 'application/json' }));

        return await this.baseService.put<void>(this.RESOURCE + '/' + sampleFirmwareRevision.id, formData);
    }

    /**
     * Deletes a sample firmware revision from the backend.
     *
     * @param id The ID of the sample firmware revision to delete.
     * @return The response from the request.
     */
    public async deleteSampleFirmwareRevision(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }

    /**
     * Fetches a sample firmware revision file data from the backend.
     *
     * @param id The ID of the sample firmware revision to fetch.
     * @return The sample firmware revision file.
     */
    public async getSampleFirmwareRevisionFile(id: number): Promise<SampleFirmwareRevisionFileDTO> {
        return await this.baseService.get<SampleFirmwareRevisionFileDTO>(this.RESOURCE + '/download/' + id);
    }

    /**
     * Creates firmware upgrade request.
     *
     * @param sampleFirmwareUpgradeRequest The firmware upgrade request.
     * @return The response from the request.
     */
    public async createSampleFirmwareUpgradeRequest(sampleFirmwareUpgradeRequest: SampleFirmwareUpgradeRequest): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE + '/upgrade', sampleFirmwareUpgradeRequest);
    }

}
